'use client'

import type { ReactNode } from 'react'
import type { DrSource } from '@/lib/dr-types'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * "From mobile signals and shelter registrations · Set by POL-12 · read +16 h 20 min"
 * (plans/00 5.7: every number can show its source on hover).
 */
export function sourceHintText(source: DrSource | null | undefined): string | null {
  if (!source) return null
  const parts: string[] = []
  if (source.systems.length > 0) parts.push(`From ${joinList(source.systems)}`)
  if (source.policy) parts.push(`Set by ${source.policy}`)
  if (source.read_label) parts.push(`read ${source.read_label}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * Wraps a figure and shows where it came from on hover and keyboard focus.
 * Inside a link pass `focusable={false}`: the link takes focus and screen
 * readers still hear the source from the visually hidden text.
 */
export function SourceHint({
  source,
  children,
  focusable = true,
}: {
  source: DrSource | null | undefined
  children: ReactNode
  focusable?: boolean
}) {
  const text = sourceHintText(source)
  if (!text) return <>{children}</>
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span
          tabIndex={focusable ? 0 : undefined}
          className='cursor-default rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          {children}
          <span className='sr-only'> ({text})</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side='bottom'>{text}</TooltipContent>
    </Tooltip>
  )
}
